/**
 * Mapeadores puros da camada de adaptação de contrato (anticorruption layer)
 * do jornal (ADR-003 / ADR-005). Traduzem o contrato do BBMNET-LICITACOES-BACKEND
 * (`api/EditaisInternal/Jornal/*` — envelope `{ editais, count }` e DTOs aninhados
 * em camelCase) para o modelo interno `Edital`/envelope `{ content, count }` que
 * a UI já consome.
 *
 * Regras (rule `testing.md`, casos defensivos de UT-030..UT-047):
 * - funções puras, sem I/O — testáveis por Vitest;
 * - identificador canônico `uniqueId` (o LICITACOES não expõe `id` numérico);
 * - props nulas/omitidas (System.Text.Json IgnoreNullValues) NÃO podem lançar;
 * - campo ausente que a UI exibe → valor neutro (`'-'`/`''`), sem quebrar render.
 */

import type {
  DocumentoEdital,
  Edital,
  EditalDocumentoDto,
  EditalExternalDto,
  EditalListEnvelope,
  EditalResumidoDto,
} from './types';

/**
 * Base de `Edital` com todos os campos preenchidos com defaults neutros. Os
 * mapeadores sobrescrevem apenas o que o DTO fornece, garantindo que nenhum
 * consumidor receba `undefined` em campo que a tipagem diz obrigatório.
 */
const baseEdital = (): Edital => ({
  uniqueId: '',
  objeto: '',
  nomeDoOrgaoPromotor: '-',
  cnpj: '',
  modalidade: '',
  utilizaVerbaFederal: false,
  amparoLegal: '',
  finalidadeDeLicitacao: '',
  numeroDoEdital: '',
  numeroDoProcesso: '',
  pregoeiro: null,
  telefone: '',
  email: '',
  unidadeCompradora: '',
  inicioDoRecebimentoDePropostas: '',
  terminoDeRecebimento: '',
  aberturaEAnalise: '',
  inicioDeLances: '',
  modoDeDisputa: '',
  situacao: '',
  criteriosDeJulgamento: '',
  valorLote: '',
  desativarChatDosLicitantesNasFases: false,
  equipeDeApoio: [],
  participantesEdital: [],
  segmentacoes: '',
  redefinicaoAutomaticaDosValores: false,
  segmentacoesLista: [],
  estado: '',
  cidade: '',
  endereco: '',
  cep: '',
  bairro: '',
  numero: '',
  complemento: '',
  valorDaProposta: 0,
  modeloDeAta: '',
  palavraChave: '',
  publishedAt: '',
  tipoValorLance: '',
  documentosDoEdital: '',
  terminoDeLances: '',
  duracaoDeLances: '',
  lotes: [],
  SituacaoNome: null,
  documentos: [],
});

// Converte um valor possivelmente ausente/nulo em string, sem lançar.
const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * Mapeia o `EditalResumidoDto` (listagem) para o modelo interno `Edital`.
 * Achata os objetos aninhados: `orgaoPromotor.nome → nomeDoOrgaoPromotor`,
 * `modalidade.name → modalidade`, `statusSalaDisputa.name → situacao`,
 * `publishAt → publishedAt`, `objeto → objeto`, `uniqueId → uniqueId`.
 */
export function mapEditalResumido(dto: EditalResumidoDto | null | undefined): Edital {
  const d = dto ?? {};
  const base = baseEdital();

  const situacaoNome = d.statusSalaDisputa?.name ?? null;

  return {
    ...base,
    uniqueId: str(d.uniqueId),
    objeto: str(d.objeto),
    nomeDoOrgaoPromotor: d.orgaoPromotor?.nome ? String(d.orgaoPromotor.nome) : '-',
    cnpj: str(d.orgaoPromotor?.documento),
    estado: str(d.orgaoPromotor?.uf),
    endereco: str(d.endereco),
    numero: str(d.numero),
    complemento: str(d.complemento),
    cep: str(d.cep),
    bairro: str(d.bairro),
    cidade: str(d.cidade),
    modalidade: str(d.modalidade?.name),
    criteriosDeJulgamento: str(d.criterioJulgamento?.name),
    segmentacoesLista: Array.isArray(d.segmentacoes) ? d.segmentacoes : [],
    situacao: str(situacaoNome),
    SituacaoNome: situacaoNome,
    publishedAt: str(d.publishAt),
    numeroDoEdital: str(d.numeroEdital),
    numeroDoProcesso: str(d.numeroProcesso),
    inicioDoRecebimentoDePropostas: str(d.inicioRecebimentoPropostas),
    terminoDeRecebimento: str(d.terminoRecebimentoPropostas),
  };
}

/**
 * Normaliza a lista de documentos do detalhe preservando a ordem: primeiro
 * `documentos`, depois `documentosComplementares`. Cada item vira um
 * `DocumentoEdital` plano. Entradas sem `downloadLink` são preservadas com link
 * vazio (a UI as trata como indisponíveis).
 */
export function mapDocumentos(
  dto: EditalExternalDto | null | undefined,
): DocumentoEdital[] {
  const d = dto ?? {};
  const documentos = Array.isArray(d.documentos) ? d.documentos : [];
  const complementares = Array.isArray(d.documentosComplementares)
    ? d.documentosComplementares
    : [];

  const mapDoc = (complementar: boolean) => (doc: EditalDocumentoDto) => ({
    nome: str(doc?.nomeArquivo ?? doc?.nomeObjeto),
    downloadLink: str(doc?.downloadLink),
    tipo: str(doc?.tipoDocumento?.name),
    complementar,
  });

  return [
    ...documentos.map(mapDoc(false)),
    ...complementares.map(mapDoc(true)),
  ];
}

/**
 * Extrai o primeiro link de download não vazio da lista de documentos. Retorna
 * `''` (vazio tratável) quando não há documento com link — sem download quebrado.
 */
export function extractPrimaryDownloadLink(documentos: DocumentoEdital[]): string {
  const first = documentos.find((doc) => str(doc?.downloadLink).trim() !== '');
  return first ? first.downloadLink : '';
}

/**
 * Mapeia o `EditalExternalDto` (detalhe) para `Edital`, reaproveitando o
 * achatamento do resumido e anexando os documentos, lotes, pregoeiro e equipe.
 * `documentosDoEdital` recebe o link de download primário (fluxo de download);
 * `documentos` recebe a lista achatada preservando a ordem. Campos objeto-escalar
 * (finalidade, modo de disputa, critério, tipo de valor, unidade compradora) são
 * achatados para o `name`/`nome` que a UI exibe.
 */
export function mapEditalExternal(dto: EditalExternalDto | null | undefined): Edital {
  const d = dto ?? {};
  const base = mapEditalResumido(dto);
  const documentos = mapDocumentos(dto);

  return {
    ...base,
    documentos,
    documentosDoEdital: extractPrimaryDownloadLink(documentos),
    lotes: Array.isArray(d.lotes) ? d.lotes : [],
    pregoeiro: d.pregoeiro ?? null,
    equipeDeApoio: Array.isArray(d.equipeDeApoio) ? d.equipeDeApoio : [],
    participantesEdital: Array.isArray(d.participantesEdital)
      ? d.participantesEdital
      : [],
    telefone: str(d.pregoeiro?.telefone),
    email: str(d.pregoeiro?.email),
    finalidadeDeLicitacao: str(d.finalidadeLicitacao?.name),
    modoDeDisputa: str(d.modoDisputa?.name),
    tipoValorLance: str(d.tipoValorLance?.name),
    unidadeCompradora: str(d.unidadeCompradora?.nome),
    amparoLegal: str(d.leisVerbaFederal?.[0]?.descricao),
  };
}

/**
 * Normaliza o envelope de listagem `{ editais, count }` do LICITACOES para o
 * formato `{ content, count }` que a `Home` consome. Preserva `count` para a
 * paginação server-side; envelope/campo nulo não lança (retorna vazio).
 */
export function mapListEnvelope(
  res: EditalListEnvelope | null | undefined,
): { content: Edital[]; count: number } {
  const editais = Array.isArray(res?.editais) ? res!.editais! : [];
  const count = typeof res?.count === 'number' ? res!.count! : editais.length;

  return {
    content: editais.map((dto) => mapEditalResumido(dto)),
    count,
  };
}

/**
 * Calcula o total de páginas para a paginação server-side. `count=57`,
 * `take=10` → 6 páginas. `take` inválido devolve 1 (sem divisão por zero).
 */
export function getTotalPages(count: number, take: number): number {
  if (!take || take <= 0) return 1;
  return Math.max(1, Math.ceil((count ?? 0) / take));
}
